/**
 * Cosmetic VARIANT CATALOGUE — the single place that says how each equipment-slot look is built.
 *
 * Rookie supplies independently dyed base layers for both body rigs. Drifter is
 * earned armor, with baked vertex colors and emissive accent primitives.
 *
 * To add a NEW modeled outfit set:
 *   1. author + build the skinned pieces into client/public/models/<body>/<part>_<set>.glb;
 *   2. add "<set>" to STYLE_VARIANTS in shared/src/rpg.ts (server validates + persists it);
 *   3. add the male `skin` and verified `femaleSkin` paths per slot here;
 *   4. extend COSMETIC_COVERS / COSMETIC_OCCLUSION_MASKS if the set encloses body regions.
 */
import type { StylePart } from "@hyperlight/shared";
import type { BodyRegion } from "./bodyModels.ts";

/** A SKINNED cosmetic: a rig-space GLB piece that binds to the avatar skeleton. */
export interface CosmeticSkin {
  skin: string;
  /** Only authored and verified female pieces opt in; legacy armor stays male. */
  femaleSkin?: string;
  /** Attached pieces are selected and removed with this wearable region. */
  attachments?: readonly string[];
  colorField?: "shirtColor" | "pantsColor";
}

export type CosmeticVariant = CosmeticSkin;

export const isSkin = (v: CosmeticVariant): v is CosmeticSkin => "skin" in v;

/** Coarse coverage is reserved for replaceable cosmetics such as hair.
 * Clothing carries precise, reversible masks for skin fully under the fabric. */
export const COSMETIC_COVERS: Partial<Record<StylePart, Record<string, readonly BodyRegion[]>>> = {
  head: {
    drifter: ["hair"],
  },
};

/** Precise, reversible body-triangle occlusion generated from actual garment coverage. */
export const COSMETIC_OCCLUSION_MASKS: Partial<Record<StylePart, Record<string, readonly string[]>>> = {
  head: { drifter: ["drifter_head"] },
  torso: { drifter: ["drifter_chest"] },
  arms: { drifter: ["drifter_hands"] },
  legs: { drifter: ["drifter_pants"] },
  feet: { drifter: ["drifter_shoes"] },
};

/** The five regions. Attached belts, packs and pauldrons cannot become extra slots. */
export const COSMETICS: Partial<Record<StylePart, Record<string, CosmeticVariant>>> = {
  head: { drifter: { skin: "/models/male/head_drifter_builds.glb" } },
  torso: {
    rookie: { skin: "/models/male/chest_rookie.glb", femaleSkin: "/models/female/chest_rookie.glb", colorField: "shirtColor" },
    drifter: { skin: "/models/male/chest_drifter_builds.glb", attachments: ["/models/male/back_drifter_builds.glb"] },
  },
  arms: { rookie: { skin: "/models/male/arms_rookie.glb", femaleSkin: "/models/female/arms_rookie.glb" }, drifter: { skin: "/models/male/hands_drifter_builds.glb", attachments: ["/models/male/shoulder_drifter_builds.glb"] } },
  legs: {
    rookie: { skin: "/models/male/pants_rookie.glb", femaleSkin: "/models/female/pants_rookie.glb", colorField: "pantsColor" },
    drifter: { skin: "/models/male/pants_drifter_builds.glb", attachments: ["/models/male/waist_drifter_builds.glb"] },
  },
  feet: { rookie: { skin: "/models/male/feet_rookie.glb", femaleSkin: "/models/female/feet_rookie.glb" }, drifter: { skin: "/models/male/shoe_drifter_builds.glb" } },
};
